/**
 * Voice input for the agent chat — a port of the CRHQ session UI's voice store
 * (control-room new/src/stores/voiceStore.ts), so the mic behaves exactly like
 * it does in CRHQ: click to record, click again to stop, the text is appended
 * to the message box (never sent on its own), a failed transcription keeps the
 * audio upstream and shows a Retry banner.
 *
 * Differences from the CRHQ original, all deliberate:
 * - The upload goes through the Cockpit API (the panel injects a transport
 *   bound to its chat link) instead of a same-origin /api/transcribe, because
 *   the CRHQ key must stay server-side.
 * - Failures without a retry (voice unavailable, recording too long) are
 *   reported through onTranscriptionError so the panel can show a message.
 */
import { create } from 'zustand';
import type { AgentTranscribeResult } from '@/services/AgentChatService';

export type VoiceState = 'idle' | 'recording' | 'transcribing';

const MIC_STORAGE_KEY = 'moniker-preferred-mic';
/** Same client-side guards as CRHQ (the server cap is 25 MB). */
const MAX_BYTES = 24 * 1024 * 1024;
const MIN_BYTES = 1000;
/**
 * Recordings stop on their own just under 2 minutes: CRHQ's shared speech
 * allowance refuses clips over 2 min (and still counts them). Whatever was
 * said is transcribed and added; pressing the mic again continues.
 */
const MAX_RECORDING_SECONDS = 115;

export interface VoiceTransport {
	transcribe: (audio: Blob) => Promise<AgentTranscribeResult>;
	retry: (recordingId: string) => Promise<AgentTranscribeResult>;
}

interface AgentVoiceState {
	voiceState: VoiceState;
	recordingDuration: number;
	lastRecordingId: string | null;
	showRetryBanner: boolean;

	audioDevices: MediaDeviceInfo[];
	selectedDeviceId: string | null;

	// Not reactive in practice — kept here like the CRHQ store.
	mediaRecorder: MediaRecorder | null;
	audioChunks: Blob[];
	recordingStartTime: number | null;
	recordingTimer: ReturnType<typeof setInterval> | null;

	transport: VoiceTransport | null;
	onTranscriptionComplete: ((text: string) => void) | null;
	onTranscriptionError: ((message: string) => void) | null;

	setTransport: (transport: VoiceTransport | null) => void;
	setShowRetryBanner: (show: boolean) => void;
	setOnTranscriptionComplete: (callback: ((text: string) => void) | null) => void;
	setOnTranscriptionError: (callback: ((message: string) => void) | null) => void;

	enumerateDevices: () => Promise<void>;
	setSelectedDevice: (deviceId: string) => void;

	startRecording: () => Promise<boolean>;
	stopRecording: () => void;
	/** Stop and throw the recording away (panel closed mid-recording). */
	cancelRecording: () => void;
	transcribeAudio: (blob: Blob) => Promise<string | null>;
	retryTranscription: () => Promise<string | null>;

	getSupportedMimeType: () => string | null;
}

function readStoredMic(): string | null {
	try {
		return typeof localStorage !== 'undefined' ? localStorage.getItem(MIC_STORAGE_KEY) : null;
	} catch {
		return null;
	}
}

export const useAgentVoiceStore = create<AgentVoiceState>((set, get) => ({
	voiceState: 'idle',
	recordingDuration: 0,
	lastRecordingId: null,
	showRetryBanner: false,
	audioDevices: [],
	selectedDeviceId: readStoredMic(),
	mediaRecorder: null,
	audioChunks: [],
	recordingStartTime: null,
	recordingTimer: null,
	transport: null,
	onTranscriptionComplete: null,
	onTranscriptionError: null,

	setTransport: (transport) => set({ transport }),
	setShowRetryBanner: (show) => set({ showRetryBanner: show }),
	setOnTranscriptionComplete: (callback) => set({ onTranscriptionComplete: callback }),
	setOnTranscriptionError: (callback) => set({ onTranscriptionError: callback }),

	enumerateDevices: async () => {
		try {
			if (!navigator.mediaDevices?.enumerateDevices) return;
			const devices = await navigator.mediaDevices.enumerateDevices();
			const audioInputs = devices.filter((d) => d.kind === 'audioinput');
			const { selectedDeviceId } = get();
			// If the selected device is no longer available, fall back to default
			const stillAvailable = audioInputs.some((d) => d.deviceId === selectedDeviceId);
			if (selectedDeviceId && !stillAvailable) {
				try { localStorage.removeItem(MIC_STORAGE_KEY); } catch { /* private mode */ }
				set({ audioDevices: audioInputs, selectedDeviceId: null });
			} else {
				set({ audioDevices: audioInputs });
			}
		} catch (err) {
			console.error('Failed to enumerate audio devices:', err);
		}
	},

	setSelectedDevice: (deviceId: string) => {
		try { localStorage.setItem(MIC_STORAGE_KEY, deviceId); } catch { /* private mode */ }
		set({ selectedDeviceId: deviceId });
	},

	getSupportedMimeType: () => {
		if (typeof MediaRecorder === 'undefined') return null;
		const types = [
			'audio/webm;codecs=opus',
			'audio/webm',
			'audio/ogg;codecs=opus',
			'audio/mp4',
			'audio/wav',
		];
		for (const type of types) {
			if (MediaRecorder.isTypeSupported(type)) return type;
		}
		return null;
	},

	startRecording: async () => {
		const mimeType = get().getSupportedMimeType();
		if (!mimeType || !navigator.mediaDevices?.getUserMedia) {
			console.error('No supported audio format');
			return false;
		}

		try {
			const { selectedDeviceId } = get();
			const audioConstraints = selectedDeviceId ? { deviceId: selectedDeviceId } : true;
			const stream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints });
			// Re-enumerate after getUserMedia to get device labels
			void get().enumerateDevices();
			const mediaRecorder = new MediaRecorder(stream, { mimeType });
			const audioChunks: Blob[] = [];
			let cancelled = false;

			mediaRecorder.ondataavailable = (e) => {
				if (e.data.size > 0) audioChunks.push(e.data);
			};

			mediaRecorder.onstop = async () => {
				stream.getTracks().forEach((track) => track.stop());
				if (cancelled) return;
				const audioBlob = new Blob(audioChunks, { type: mimeType });
				// Transcribe — text handling is done by the caller's callback
				await get().transcribeAudio(audioBlob);
			};
			(mediaRecorder as any).__cancel = () => { cancelled = true; };

			mediaRecorder.start(1000);

			const recordingStartTime = Date.now();
			const recordingTimer = setInterval(() => {
				const seconds = Math.floor((Date.now() - recordingStartTime) / 1000);
				set({ recordingDuration: seconds });
				if (seconds >= MAX_RECORDING_SECONDS && get().mediaRecorder === mediaRecorder) {
					get().stopRecording();
					get().onTranscriptionError?.('Voice notes stop at 2 minutes. What you said so far is being added. Press the mic to keep going.');
				}
			}, 1000);

			set({
				mediaRecorder,
				audioChunks,
				recordingStartTime,
				recordingTimer,
				voiceState: 'recording',
				recordingDuration: 0,
			});
			return true;
		} catch (error) {
			console.error('Microphone error:', error);
			return false;
		}
	},

	stopRecording: () => {
		const { mediaRecorder, recordingTimer } = get();
		if (recordingTimer) clearInterval(recordingTimer);
		if (mediaRecorder && mediaRecorder.state !== 'inactive') mediaRecorder.stop();
		set({ voiceState: 'idle', mediaRecorder: null, recordingTimer: null, recordingStartTime: null });
	},

	cancelRecording: () => {
		const { mediaRecorder } = get();
		if (mediaRecorder) (mediaRecorder as any).__cancel?.();
		get().stopRecording();
	},

	transcribeAudio: async (blob) => {
		if (!blob || blob.size === 0) {
			console.error('Recording was empty');
			return null;
		}
		if (blob.size > MAX_BYTES) {
			get().onTranscriptionError?.('That recording is too long. Please record a shorter voice note.');
			return null;
		}
		if (blob.size < MIN_BYTES) {
			console.error('Recording too short:', blob.size);
			return null;
		}
		const { transport } = get();
		if (!transport) {
			get().onTranscriptionError?.('Voice input is not available right now. Please type your message.');
			return null;
		}

		set({ voiceState: 'transcribing' });
		try {
			const result = await withTimeout(transport.transcribe(blob), 120000);
			if (result.success && result.text) {
				const callback = get().onTranscriptionComplete;
				set({ voiceState: 'idle', lastRecordingId: null, showRetryBanner: false });
				callback?.(result.text);
				return result.text;
			}
			if (result.success) {
				// Nothing was said (silence) — same as CRHQ: no text, no error.
				set({ voiceState: 'idle' });
				return null;
			}
			if (result.recordingId) {
				set({ voiceState: 'idle', lastRecordingId: result.recordingId, showRetryBanner: true });
			} else {
				set({ voiceState: 'idle' });
				get().onTranscriptionError?.(result.message || 'Transcription failed.');
			}
			return null;
		} catch (error) {
			console.error('Transcription error:', error);
			set({ voiceState: 'idle' });
			get().onTranscriptionError?.('Transcription failed. Please try again.');
			return null;
		}
	},

	retryTranscription: async () => {
		const { lastRecordingId, transport } = get();
		if (!lastRecordingId || !transport) return null;

		set({ voiceState: 'transcribing', showRetryBanner: false });
		try {
			const result = await withTimeout(transport.retry(lastRecordingId), 120000);
			if (result.success) {
				set({ voiceState: 'idle', lastRecordingId: null, showRetryBanner: false });
				return result.text || null;
			}
			if (result.recordingId) {
				set({ voiceState: 'idle', showRetryBanner: true });
			} else {
				// Expired upstream (or voice switched off): retrying cannot work.
				set({ voiceState: 'idle', lastRecordingId: null, showRetryBanner: false });
				get().onTranscriptionError?.(result.message || 'Transcription failed.');
			}
			return null;
		} catch (error) {
			console.error('Retry transcription error:', error);
			set({ voiceState: 'idle', showRetryBanner: true });
			return null;
		}
	},
}));

function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
	return new Promise<T>((resolve, reject) => {
		const t = setTimeout(() => reject(new Error('timeout')), ms);
		p.then((v) => { clearTimeout(t); resolve(v); }, (e) => { clearTimeout(t); reject(e); });
	});
}

// Listen for device changes (connect/disconnect)
if (typeof navigator !== 'undefined' && navigator.mediaDevices?.addEventListener) {
	navigator.mediaDevices.addEventListener('devicechange', () => {
		void useAgentVoiceStore.getState().enumerateDevices();
	});
}
